import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  CogIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { 
  validateSectionConfig, 
  getFieldWarnings, 
  hasUnsavedChanges, 
  getChangedFields 
} from '../utils/validation';
import { SettingTooltip } from '../components/Tooltip';
import { getSettingTooltip } from '../utils/settingTooltips';
import ModelSwitcher from '../components/ModelSwitcher';

const Settings = () => {
  const [activeSection, setActiveSection] = useState('models');
  const [localConfig, setLocalConfig] = useState({});
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [fieldWarnings, setFieldWarnings] = useState({});
  
  const queryClient = useQueryClient();

  // Fetch current configuration
  const { data: configData, isLoading, error } = useQuery({
    queryKey: ['configuration'],
    queryFn: async () => {
      const response = await api.get('/config/');
      return response.data;
    }
  });

  // Fetch configuration schema
  const { data: schemaData } = useQuery({
    queryKey: ['configuration-schema'],
    queryFn: async () => {
      const response = await api.get('/config/schema');
      return response.data;
    }
  });

  // Update configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: async ({ section, updates }) => {
      const response = await api.put('/config/', { section, updates });
      return response.data;
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries(['configuration']);
        setUnsavedChanges(false);
        setValidationErrors({});
        setFieldWarnings({});
      } else {
        setValidationErrors({ [activeSection]: data.errors || ['Update failed'] });
      }
    },
    onError: (error) => {
      setValidationErrors({ 
        [activeSection]: [error.response?.data?.detail || 'Failed to update configuration'] 
      });
    }
  });

  // Reset configuration mutation
  const resetConfigMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/config/reset');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['configuration']);
      setLocalConfig({});
      setUnsavedChanges(false);
      setValidationErrors({});
      setFieldWarnings({});
    }
  });

  // Initialize local config when data loads
  useEffect(() => {
    if (configData?.configuration) {
      setLocalConfig(configData.configuration);
    }
  }, [configData]);

  // Handle input changes
  const handleInputChange = (section, field, value) => {
    let newConfig = {
      ...localConfig,
      [section]: {
        ...localConfig[section],
        [field]: value
      }
    };
    
    // Special handling for LLM provider changes
    if (section === 'models' && field === 'llm_provider') {
      // Set appropriate default model when switching providers
      if (value === 'bedrock' && !newConfig.models.bedrock_model_id) {
        newConfig.models.bedrock_model_id = 'amazon.nova-pro-v1:0';
      } else if (value === 'anthropic' && !newConfig.models.anthropic_model_id) {
        newConfig.models.anthropic_model_id = 'claude-sonnet-4-20250514';
      }
      
      // Clear validation errors when switching providers
      setValidationErrors(prev => ({
        ...prev,
        [section]: []
      }));
      
      // Clear field warnings as well
      setFieldWarnings(prev => {
        const newWarnings = { ...prev };
        Object.keys(newWarnings).forEach(key => {
          if (key.startsWith(`${section}.`)) {
            delete newWarnings[key];
          }
        });
        return newWarnings;
      });
    }
    
    setLocalConfig(newConfig);
    
    // Check for unsaved changes
    const hasChanges = hasUnsavedChanges(configData?.configuration || {}, newConfig);
    setUnsavedChanges(hasChanges);
    
    // Validate the section
    if (schemaData?.schema) {
      const sectionErrors = validateSectionConfig(section, newConfig[section] || {}, schemaData.schema);
      setValidationErrors(prev => ({
        ...prev,
        [section]: sectionErrors
      }));
      
      // Get field warnings
      const warnings = getFieldWarnings(section, field, value);
      setFieldWarnings(prev => ({
        ...prev,
        [`${section}.${field}`]: warnings
      }));
    }
  };

  // Save changes for current section
  const handleSaveSection = () => {
    const sectionConfig = localConfig[activeSection] || {};
    
    // Validate before saving
    if (schemaData?.schema) {
      const errors = validateSectionConfig(activeSection, sectionConfig, schemaData.schema);
      if (errors.length > 0) {
        setValidationErrors(prev => ({
          ...prev,
          [activeSection]: errors
        }));
        return;
      }
    }
    
    const originalConfig = configData?.configuration?.[activeSection] || {};
    const changedFields = getChangedFields(originalConfig, sectionConfig);
    
    if (Object.keys(changedFields).length > 0) {
      const updates = {};
      Object.keys(changedFields).forEach(key => {
        updates[key] = changedFields[key].to;
      });
      
      updateConfigMutation.mutate({ section: activeSection, updates });
    }
  };


  // Render form field based on schema
  const renderField = (section, fieldName, fieldConfig) => {
    let value = localConfig[section]?.[fieldName] ?? fieldConfig.default;
    
    // Ensure we have valid defaults for model fields
    if (section === 'models') {
      if (fieldName === 'bedrock_model_id' && !value) {
        value = 'amazon.nova-pro-v1:0';
      } else if (fieldName === 'anthropic_model_id' && !value) {
        value = 'claude-sonnet-4-20250514';
      }
    }
    const fieldId = `${section}-${fieldName}`;
    const warnings = fieldWarnings[`${section}.${fieldName}`] || [];
    const hasFieldError = validationErrors[section]?.some(error => 
      error.toLowerCase().includes(fieldConfig.label.toLowerCase())
    );

    switch (fieldConfig.type) {
      case 'boolean':
        return (
          <div>
            <div className="flex items-center">
              <input
                id={fieldId}
                type="checkbox"
                checked={value}
                onChange={(e) => handleInputChange(section, fieldName, e.target.checked)}
                className={`h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded ${
                  hasFieldError ? 'border-red-300' : ''
                }`}
              />
              <div className="ml-2 flex items-center">
                <label htmlFor={fieldId} className="block text-sm text-gray-900">
                  {fieldConfig.label}
                </label>
                <SettingTooltip 
                  label="" 
                  tooltip={getSettingTooltip(section, fieldName)}
                  position="right"
                />
              </div>
            </div>
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'select':
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <select
              id={fieldId}
              value={value}
              onChange={(e) => handleInputChange(section, fieldName, e.target.value)}
              className={`mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md ${
                hasFieldError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            >
              {fieldConfig.options.map(option => (
                <option key={option} value={option}>{option}</option>
              ))}
            </select>
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      case 'number':
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <input
              id={fieldId}
              type="number"
              value={value}
              min={fieldConfig.min}
              max={fieldConfig.max}
              step={fieldConfig.step || 1}
              onChange={(e) => handleInputChange(section, fieldName, parseFloat(e.target.value) || 0)}
              className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${
                hasFieldError ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            />
            {fieldConfig.min !== undefined || fieldConfig.max !== undefined ? (
              <div className="mt-1 text-xs text-gray-500">
                {fieldConfig.min !== undefined && fieldConfig.max !== undefined 
                  ? `Range: ${fieldConfig.min} - ${fieldConfig.max}`
                  : fieldConfig.min !== undefined 
                    ? `Minimum: ${fieldConfig.min}`
                    : `Maximum: ${fieldConfig.max}`
                }
              </div>
            ) : null}
            {warnings.length > 0 && (
              <div className="mt-1">
                {warnings.map((warning, index) => (
                  <div key={index} className="flex items-center text-amber-600 text-xs">
                    <InformationCircleIcon className="h-3 w-3 mr-1" />
                    {warning}
                  </div>
                ))}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div>
            <div className="mb-1">
              <SettingTooltip 
                label={fieldConfig.label}
                tooltip={getSettingTooltip(section, fieldName)}
                position="right"
              />
            </div>
            <input
              id={fieldId}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(section, fieldName, e.target.value)}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-600" />
          <span>Loading configuration...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Failed to Load Configuration</h2>
          <p className="text-gray-600">{error.message}</p>
        </div>
      </div>
    );
  }

  const schema = schemaData?.schema || {};
  const sections = Object.keys(schema);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <CogIcon className="h-8 w-8 mr-3 text-blue-600" />
              System Configuration
            </h1>
            <p className="mt-2 text-gray-600">
              Configure models, parameters, and system behavior for the DECLASS platform.
            </p>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="flex">
              {/* Sidebar */}
              <div className="w-64 bg-gray-50 border-r border-gray-200">
                <nav className="space-y-1 p-4">
                  {sections.map((section) => (
                    <button
                      key={section}
                      onClick={() => setActiveSection(section)}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                        activeSection === section
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span>{schema[section]?.title || section}</span>
                        {unsavedChanges && activeSection === section && (
                          <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                        )}
                      </div>
                    </button>
                  ))}
                </nav>
              </div>

              {/* Main Content */}
              <div className="flex-1 p-6">
                {schema[activeSection] && (
                  <div>
                    <div className="mb-6">
                      <h2 className="text-xl font-semibold text-gray-900">
                        {schema[activeSection].title}
                      </h2>
                      <p className="mt-1 text-gray-600">
                        {schema[activeSection].description}
                      </p>
                    </div>

                    {/* Validation Errors */}
                    {validationErrors[activeSection] && validationErrors[activeSection].length > 0 && (
                      <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                              Configuration Errors
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                              <ul className="list-disc list-inside space-y-1">
                                {validationErrors[activeSection].map((error, index) => (
                                  <li key={index}>{error}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Weight Validation Warning */}
                    {validationErrors[activeSection]?.some(error => error.includes('weight')) && (
                      <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">
                              Weight Configuration Warning
                            </h3>
                            <p className="mt-1 text-sm text-yellow-700">
                              {activeSection === 'search' 
                                ? 'Original Score Weight + Enhancement Weight must equal 1.0'
                                : 'Keyword Match Weight + Semantic Match Weight must equal 1.0'
                              }
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Model Switcher for Models Section */}
                    {activeSection === 'models' && (
                      <div className="mb-6">
                        <ModelSwitcher showAsCard={true} />
                      </div>
                    )}

                    {/* Configuration Fields */}
                    <div className="space-y-6">
                      {Object.entries(schema[activeSection].fields || {}).map(([fieldName, fieldConfig]) => {
                        // Conditional rendering for model fields based on provider selection
                        if (activeSection === 'models') {
                          const currentProvider = localConfig.models?.llm_provider || 'bedrock';
                          
                          // Only show Bedrock model field when Bedrock is selected
                          if (fieldName === 'bedrock_model_id' && currentProvider !== 'bedrock') {
                            return null;
                          }
                          
                          // Only show Anthropic model field when Anthropic is selected
                          if (fieldName === 'anthropic_model_id' && currentProvider !== 'anthropic') {
                            return null;
                          }
                        }
                        
                        return (
                          <div key={fieldName} className="border-b border-gray-200 pb-4">
                            {renderField(activeSection, fieldName, fieldConfig)}
                            {fieldConfig.description && (
                              <p className="mt-1 text-xs text-gray-500">
                                {fieldConfig.description}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Actions */}
                    <div className="mt-8 flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={handleSaveSection}
                          disabled={!unsavedChanges || updateConfigMutation.isPending || (validationErrors[activeSection]?.length > 0)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {updateConfigMutation.isPending ? (
                            <>
                              <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <CheckCircleIcon className="h-4 w-4 mr-2" />
                              Save Changes
                            </>
                          )}
                        </button>

                        {unsavedChanges && (
                          <span className="text-sm text-orange-600 font-medium">
                            You have unsaved changes
                          </span>
                        )}
                      </div>

                      <button
                        onClick={() => resetConfigMutation.mutate()}
                        disabled={resetConfigMutation.isPending}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <DocumentDuplicateIcon className="h-4 w-4 mr-2" />
                        Reset to Defaults
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Configuration Tips */}
          <div className="mt-8 bg-blue-50 border border-blue-200 rounded-md p-4">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Configuration Tips</h3>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Changes to model configurations may require system restart for full effect</li>
              <li>• Higher temperature values increase randomness in AI responses</li>
              <li>• Larger embedding models provide better accuracy but slower performance</li>
              <li>• Weight values in search and SCG sections must sum to 1.0</li>
              <li>• OCR processing significantly increases PDF processing time</li>
              <li>• Advanced retrieval algorithms improve rule matching accuracy</li>
              <li>• Semantic SCG retrieval uses vector embeddings for better rule matching</li>
              <li>• Higher similarity thresholds are more selective but may miss relevant content</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;